import jwt_decode from 'jwt-decode';
import { authHeader, apiClient } from '../helpers';

// Login function - now using apiClient
const login = (userName: string, password: string) => {
  // Login không cần token nên dùng fetch bình thường
  return fetch(`${process.env.REACT_APP_API_URL}/api/teachers/login`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420'
    },
    body: JSON.stringify({ userName, password })
  })
    .then(response => response.json())
    .then((data) => {
      if (!data.accessToken || !data.refreshToken) {
        throw new Error('Invalid login response');
      }
      // Lưu accessToken và refreshToken vào localStorage (tokens are already strings, no need to stringify)
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      return data;
    })
    .catch(error => {
      console.error('Login error:', error);
      throw error;
    });
};

// Refresh token function - now consistent with apiClient.ts
const refreshToken = () => {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    return Promise.reject('No refresh token available');
  }

  return fetch(`${process.env.REACT_APP_API_URL}/api/teachers/refresh-token`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420'
    },
    body: JSON.stringify({ refreshToken: storedRefreshToken })
  })
    .then(response => response.json())
    .then((data) => {
      if (!data.accessToken) {
        throw new Error('Invalid refresh token response');
      }
      // Lưu lại accessToken mới (tokens are already strings, no need to stringify)
      localStorage.setItem('accessToken', data.accessToken);
      // Nếu có refreshToken mới thì lưu
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      return data.accessToken;
    })
    .catch(error => {
      console.error('Refresh token error:', error);
      // Nếu refresh token thất bại, xóa hết và logout
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      throw error;
    });
};

const signup = (user: any) => {
  // Signup cần token nên dùng apiClient
  return apiClient.post('/api/teachers/register', {
    ...user, 
    role: 2 // role=2 là giáo viên
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Signup error:', error);
      throw error;
    });
};

const create = (user: any) => {
  // Create user - allow specifying role
  return apiClient.post('/api/teachers/register', user)
    .then(response => response.data)
    .catch(error => {
      console.error('Create user error:', error);
      throw error;
    });
};

const logout = () => {
  // Xóa accessToken và refreshToken khỏi localStorage khi logout
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userInfo');
};

const uploadAvatar = (avatarBase64: string) => {
  // Convert base64 to blob
  const base64Data = avatarBase64.split(',')[1] || avatarBase64;
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'image/jpeg' });
  
  const formData = new FormData();
  formData.append('file', blob, 'avatar.jpg');
  
  return apiClient.post('/api/users/upload-avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Error uploading avatar:', error);
      throw error;
    });
};

export const userService = {
  login,
  logout,
  signup,
  create,
  getAll,
  getById,
  update,
  delete: delete_user,
  updatePassword,
  refreshToken,
  getAllStudents,
  uploadAvatar,
  getCurrentUser,
};

// Use apiClient with auto token refresh for GET requests
function getAll() {
  return apiClient.get('/api/teachers/')
    .then(response => response.data)
    .catch(error => {
      console.error('Error getting all teachers:', error);
      throw error;
    });
}

function getById(id: any) {
  return apiClient.get(`/api/teachers/${id}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error getting teacher by id:', error);
      throw error;
    });
}

function update(user: any) {
  return apiClient.put(`/api/teachers/${user.id}`, user)
    .then(response => response.data)
    .catch(error => {
      console.error('Error updating teacher:', error);
      throw error;
    });
}

function updatePassword(userId: number, password: string) {
  return apiClient.put(`/api/teachers/${userId}/password`, { password })
    .then(response => response.data)
    .catch(error => {
      console.error('Error updating password:', error);
      throw error;
    });
}

// prefixed function name with underscore because delete is a reserved word in javascript
function delete_user(id: any) {
  return apiClient.delete(`/api/users/${id}`)
    .then(response => response.data)
    .catch(error => {
      console.error('Error deleting user:', error);
      throw error;
    });
}

// REMOVED: handleResponse function is no longer needed
// All requests now go through apiClient which handles token refresh automatically

function getAllStudents() {
  return apiClient.get('/api/teachers/role/2')
    .then(response => response.data)
    .catch(error => {
      console.error('Error getting all students:', error);
      throw error;
    });
}

function getCurrentUser() {
  return apiClient.get('/api/users/me')
    .then(response => response.data)
    .catch(error => {
      console.error('Error getting current user:', error);
      throw error;
    });
}

